import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Translate } from 'phosphor-react';
import { updateSeoMetadata } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const Blog = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Trading Guides & Resources | GENI',
      description: 'Explore trading guides, strategies, and resources to improve your trading performance.',
      path: '/blog',
      lang: 'en'
    });
  }, []);

  const englishArticles = [
    {
      path: '/what-is-geni',
      title: 'What is GENI?',
      description: 'Complete explanation of what GENI does and how it helps traders improve.'
    },
    {
      path: '/automated-trading-journal',
      title: 'Automated Trading Journal',
      description: 'Why automated journals outperform manual tracking for serious traders.'
    },
    {
      path: '/crypto-trading-journal',
      title: 'Crypto Trading Journal',
      description: 'How to track and analyze your cryptocurrency trades effectively.'
    },
    {
      path: '/how-to-analyze-your-trades',
      title: 'How to Analyze Your Trades',
      description: 'A practical guide to extracting insights from your trading data.'
    },
    {
      path: '/manual-vs-automated-trading-journal',
      title: 'Manual vs Automated Trading Journal',
      description: 'Compare journaling methods and find what works best for you.'
    },
    {
      path: '/why-traders-fail-to-improve',
      title: 'Why Traders Fail to Improve',
      description: 'The hidden reasons most traders plateau and how to break through.'
    },
    {
      path: '/how-to-improve-day-trading',
      title: 'How to Improve Day Trading',
      description: 'Data-driven techniques to enhance your day trading performance.'
    },
    {
      path: '/profit-factor-trading',
      title: 'Profit Factor in Trading',
      description: 'Understanding the key metric that reveals trading quality.'
    },
    {
      path: '/win-rate-trading',
      title: 'Win Rate in Trading',
      description: 'Why winning more trades doesn\'t always mean making more money.'
    },
    {
      path: '/overtrading-explained',
      title: 'Overtrading Explained',
      description: 'How excessive trading destroys accounts and how to stop it.'
    },
    {
      path: '/how-many-trades-to-validate-a-strategy',
      title: 'How Many Trades to Validate a Strategy',
      description: 'The sample size you need before trusting your trading system.'
    },
    {
      path: '/how-to-find-a-trading-setup',
      title: 'How to Find a Trading Setup',
      description: 'A systematic approach to discovering your edge in the market.'
    },
    {
      path: '/improve-risk-reward-ratio',
      title: 'Improve Your Risk Reward Ratio',
      description: 'Techniques to get better returns relative to your risk.'
    },
    {
      path: '/why-consistency-is-hard-in-trading',
      title: 'Why Consistency is Hard in Trading',
      description: 'The psychological barriers to consistent trading and how to overcome them.'
    }
  ];

  const portugueseArticles = [
    {
      path: '/pt/diario-de-trades-automatico',
      title: 'Diário de Trades Automático',
      description: 'Como um diário automático pode melhorar sua consistência no trading.'
    },
    {
      path: '/pt/diario-de-trades-cripto',
      title: 'Diário de Trades Cripto',
      description: 'Guia completo para registrar e analisar suas operações com criptomoedas.'
    },
    {
      path: '/pt/como-analisar-trades',
      title: 'Como Analisar Trades',
      description: 'Técnicas práticas para extrair insights das suas operações.'
    },
    {
      path: '/pt/como-melhorar-no-day-trade',
      title: 'Como Melhorar no Day Trade',
      description: 'Processo de melhoria baseado em dados reais de performance.'
    },
    {
      path: '/pt/como-melhorar-risk-reward',
      title: 'Como Melhorar Risk Reward',
      description: 'Estratégias para otimizar sua relação risco/retorno.'
    },
    {
      path: '/pt/profit-factor-trading',
      title: 'Profit Factor no Trading',
      description: 'Entenda a métrica que revela a qualidade real das suas operações.'
    },
    {
      path: '/pt/win-rate-trading',
      title: 'Win Rate no Trading',
      description: 'Por que acertar mais trades nem sempre significa lucrar mais.'
    },
    {
      path: '/pt/overtrading',
      title: 'Overtrading',
      description: 'Como o excesso de operações destrói contas e como evitar.'
    },
    {
      path: '/pt/quantos-trades-para-validar-estrategia',
      title: 'Quantos Trades Para Validar Estratégia',
      description: 'A quantidade mínima de operações para confiar no seu sistema.'
    },
    {
      path: '/pt/como-encontrar-um-setup',
      title: 'Como Encontrar um Setup',
      description: 'Processo sistemático para descobrir seu edge no mercado.'
    },
    {
      path: '/pt/por-que-traders-nao-evoluem',
      title: 'Por Que Traders Não Evoluem',
      description: 'Os erros ocultos que impedem a evolução e como superá-los.'
    },
    {
      path: '/pt/por-que-consistencia-e-dificil-no-trading',
      title: 'Por Que Consistência é Difícil',
      description: 'As barreiras psicológicas para manter consistência no trading.'
    },
    {
      path: '/pt/diario-de-trades-manual-vs-automatico',
      title: 'Diário Manual vs Automático',
      description: 'Comparação completa para escolher o melhor método de registro.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation - Same as Landing.js */}
      <nav className="w-full px-6 py-5 flex items-center justify-between max-w-[1200px] mx-auto">
        <Link to="/" className="flex items-center flex-shrink-0">
          <img 
            src={LOGO_HORIZONTAL} 
            alt="GENI"
            className="h-10 md:h-11 lg:h-12 w-auto object-contain"
          />
        </Link>
        <div className="flex items-center gap-4">
          <Link 
            to="/login" 
            className="text-white/60 hover:text-white text-sm font-medium transition-colors"
          >
            Login
          </Link>
          <Link 
            to="/signup" 
            className="px-4 py-2 bg-white text-black text-sm font-semibold rounded-sm hover:bg-white/90 transition-colors"
          >
            Start tracking
          </Link>
        </div>
      </nav>

      <main className="max-w-[1200px] mx-auto px-6 py-16">
        {/* Header */}
        <header className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Trading Resources & Guides
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Learn how to track, analyze, and improve your trading performance with data-driven insights.
          </p>
        </header>

        {/* English Section */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <BookOpen size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">English Content</h2>
            <span className="text-sm text-white/40 ml-2">({englishArticles.length} articles)</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {englishArticles.map((article, index) => (
              <Link
                key={index}
                to={article.path}
                className="group p-5 bg-white/[0.02] border border-white/10 rounded-xl hover:border-[#00E676]/50 hover:bg-white/[0.04] transition-all"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00E676] transition-colors flex items-center gap-2">
                  {article.title}
                  <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {article.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Portuguese Section */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Translate size={20} className="text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold">Conteúdo em Português</h2>
            <span className="text-sm text-white/40 ml-2">({portugueseArticles.length} artigos)</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {portugueseArticles.map((article, index) => (
              <Link
                key={index}
                to={article.path}
                className="group p-5 bg-white/[0.02] border border-white/10 rounded-xl hover:border-blue-500/50 hover:bg-white/[0.04] transition-all"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors flex items-center gap-2">
                  {article.title}
                  <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {article.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-12 px-8 bg-gradient-to-br from-[#00E676]/10 via-transparent to-transparent border border-[#00E676]/20 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Track Your Trades?</h2>
          <p className="text-white/60 mb-6 max-w-xl mx-auto">
            Stop guessing. Start understanding your trading with real data.
          </p>
          <Link 
            to="/signup"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E676] text-black font-semibold rounded-lg hover:bg-[#00E676]/90 transition-colors"
          >
            Start Tracking Free
            <ArrowRight size={18} weight="bold" />
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 mt-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src={LOGO_HORIZONTAL}
                alt="GENI" 
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-white/30">© 2025 GENI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
